import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './MyProfilePage.css';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next';


const PublicProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState(null);
  const [visibility, setVisibility] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, 'users', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        if (data.stats) setStats(data.stats);
        if (data.statsVisibility) setVisibility(data.statsVisibility);
      }

      const achievementsSnap = await getDocs(collection(db, 'users', id, 'achievements'));
      setAchievements(achievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const tournamentsSnap = await getDocs(collection(db, 'users', id, 'tournaments'));
      setTournaments(tournamentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    fetchData();
  }, [id]);

  if (!userData) return <div>{t('profile.loadingProfile')}</div>;

  return (
    <div className="my-profile-container">
      <div className="top-profile-image">
        <div className="my-profile-banner">
          <img
            src={userData.bannerPicture || '/default-banner.jpg'}
            alt="Banner"
            className="my-banner-img"
          />
          <div className="my-profile-picture-wrapper">
            <img
              src={userData.profilePicture || '/default-avatar.png'}
              alt="Profile"
              className={`my-profile-picture ${userData.division?.toLowerCase().replace(' ', '-') || ''}`}
            />
          </div>
        </div>
      </div>

      <div className="my-profile-info-box">
        <h2>{userData.pseudo}</h2>
        <p><strong>{t('profile.division')}:</strong> {userData.division || t('profile.unknown')}</p>
        {userData.masterDuelId && (
          <p><strong>{t('profile.masterDuelId')}:</strong> {userData.masterDuelId}</p>
        )}
      </div>


      {stats && (
        <div className="my-profile-stats-box">
          <h3 className="section-title">{t('profile.stats')}</h3>
          <div className="stats-list">
            {visibility.matches && <div className="stat-item">{t('profile.matches')}: {stats.matches}</div>}
            {visibility.wins && <div className="stat-item">{t('profile.wins')}: {stats.wins}</div>}
            {visibility.losses && <div className="stat-item">{t('profile.losses')}: {stats.losses}</div>}
            {visibility.itemsBought && <div className="stat-item">{t('profile.itemsBought')}: {stats.itemsBought}</div>}
          </div>
        </div>
      )}

      <div className="profile-section">
        <h3 className="section-title">{t('profile.tournaments')}</h3>
        {tournaments.length > 0 ? (
          <div className="achievements-list">
            {tournaments.map(tournament => (
              <div key={tournament.id} className="achievement-row">
                <img
                  src={tournament.tournamentIcon || '/default-tournament.png'}
                  alt="Tournament Icon"
                  className="achievement-icon"
                />
                <div className="achievement-info">
                  <div className="achievement-name">{tournament.name || t('profile.unnamedTournament')}</div>
                  <div className="achievement-description">
                    {t('profile.ranking')}: {tournament.placement || t('profile.participation')}<br />
                    {t('profile.date')}: {tournament.date?.seconds ? new Date(tournament.date.seconds * 1000).toLocaleDateString() : t('profile.unknown')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>{t('profile.noTournaments')}</p>
        )}
      </div>

      <div className="profile-section">
        <h3 className="section-title">{t('profile.achievements')}</h3>
        {achievements.length > 0 ? (
          <div className="achievements-list">
            {achievements.map(a => (
              <div key={a.id} className="achievement-row">
                <img
                  src={a.icon}
                  alt={a.name}
                  className={`achievement-icon ${a.special ? 'special-glow' : ''}`}
                />
                <div className="achievement-info">
                  <div className="achievement-name">{a.name}</div>
                  <div className="achievement-description">{a.description}</div>
                </div>
                <div className="achievement-date">
                  {a.dateEarned?.seconds ? new Date(a.dateEarned.seconds * 1000).toLocaleDateString() : ''}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>{t('profile.noAchievements')}</p>
        )}
      </div>

      <div className="my-profile-actions">
        <button
          className="back-button"
          onClick={() => {
            playSound('return');
            navigate('/duelists');
          }}
        >
          {t('profile.back')}
        </button>
      </div>
    </div>
  );
};

export default PublicProfilePage;
